import { ArrowDownRight, ArrowRight, ArrowUpRight } from 'lucide-react';
import { StatusBadge } from '../../components/StatusBadge';
import { LAB_PANELS } from '../../data/labCatalog';
import type { LabResult, LabTest } from '../../data/labs';
import { formatNumber, STATUS_LABELS, statusTone, urr, type LabStatus } from '../../lib/labs';

export function panelName(panel: string) {
  return LAB_PANELS.find((item) => item.id === panel)?.name ?? panel;
}

/** Tests grouped by panel in catalog order; hidden tests left out unless asked for. */
export function groupByPanel(tests: LabTest[], { includeHidden = false } = {}) {
  const groups: { panel: string; tests: LabTest[] }[] = [];
  const order = (panel: string) => {
    const index = LAB_PANELS.findIndex((item) => item.id === panel);
    return index === -1 ? LAB_PANELS.length : index;
  };
  for (const test of [...tests].sort((a, b) => order(a.panel) - order(b.panel) || a.sort_order - b.sort_order || a.name.localeCompare(b.name))) {
    if (test.hidden && !includeHidden) continue;
    let group = groups.find((item) => item.panel === test.panel);
    if (!group) {
      group = { panel: test.panel, tests: [] };
      groups.push(group);
    }
    group.tests.push(test);
  }
  return groups;
}

/** The latest result for `testId` from a report dated before `date`. */
export function previousResult(results: LabResult[], testId: string | null, date: string) {
  let found: LabResult | undefined;
  for (const result of results) if (result.test_id === testId && result.result_date < date) found = result;
  return found;
}

/** URR for a report, from its before- and after-dialysis urea results. */
export function reportUrr(results: LabResult[], tests: LabTest[]) {
  const id = (code: string) => tests.find((test) => test.code === code)?.id;
  const value = (code: string) => results.find((result) => result.test_id === id(code))?.numeric_value;
  return urr(value('UREA'), value('UREA_POST'));
}

export function LabStatusBadge({ status }: { status: LabStatus | null }) {
  if (!status || status === 'unknown') return null;
  return <StatusBadge tone={statusTone(status)}>{STATUS_LABELS[status]}</StatusBadge>;
}

export function resultText(result: Pick<LabResult, 'numeric_value' | 'raw_value' | 'qualitative_value'>) {
  if (result.numeric_value != null) return formatNumber(result.numeric_value);
  return result.qualitative_value ?? result.raw_value ?? '';
}

/** "↑ 0.6 from 4.8" against the previous report's value. */
export function Change({ current, previous }: { current: LabResult; previous: LabResult | undefined }) {
  if (!previous || current.numeric_value == null || previous.numeric_value == null) return null;
  const diff = Math.round((current.numeric_value - previous.numeric_value) * 100) / 100;
  const Icon = diff > 0 ? ArrowUpRight : diff < 0 ? ArrowDownRight : ArrowRight;
  return (
    <span className="inline-flex items-center gap-0.5 text-xs text-clinical-muted">
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {diff === 0 ? 'Same as' : `${diff > 0 ? '+' : '−'}${formatNumber(Math.abs(diff))} from`} {formatNumber(previous.numeric_value)}
    </span>
  );
}
